import React, { useEffect, useMemo, useState } from "react";

import styles from "./Buyurtmalar.module.css";
import { fetchAdminShopOrder, fetchAdminShopOrders } from "../shop/api";
import { formatPrice } from "../shop/utils";


function normalizeOrder(raw = {}) {
  return {
    group_token: raw.group_token || "",
    full_name: raw.full_name || "",
    phone: raw.phone || "",
    address: raw.address || "",
    comment: raw.comment || "",
    created_at: raw.created_at || "",
    status: raw.status || "",
    sold_by_username: raw.sold_by_username || "",
    items: Array.isArray(raw.items)
      ? raw.items.map((item) => ({
          product_id: item.product_id,
          product_name: item.product_name || "",
          product_sku: item.product_sku || "",
          product_price: item.product_price || "0",
          quantity: Number(item.quantity || 0),
        }))
      : [],
  };
}


function formatDate(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString("uz-UZ", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}


function statusLabel(value) {
  if (value === "confirmed") return "Tasdiqlangan";
  if (value === "cancelled") return "Bekor qilingan";
  return value || "Tarix";
}


export default function BozorTarixPage() {
  const [orders, setOrders] = useState([]);
  const [selectedToken, setSelectedToken] = useState("");
  const [detail, setDetail] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [error, setError] = useState("");
  const [mobileView, setMobileView] = useState("list"); // 'list' | 'detail'

  useEffect(() => {
    const timer = window.setTimeout(() => setSearchQuery(searchTerm.trim()), 250);
    return () => window.clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    let cancelled = false;
    async function loadOrders() {
      setLoading(true);
      setError("");
      try {
        const data = await fetchAdminShopOrders({
          bucket: "history",
          source: "bozor",
          search: searchQuery || undefined,
        });
        if (cancelled) return;
        const nextOrders = Array.isArray(data) ? data.map(normalizeOrder) : [];
        setOrders(nextOrders);
        setSelectedToken((current) => {
          const stillExists = current && nextOrders.some((item) => item.group_token === current);
          if (!stillExists) setMobileView("list");
          return stillExists ? current : (nextOrders[0]?.group_token || "");
        });
        if (!nextOrders.length) {
          setDetail(null);
        }
      } catch (_) {
        if (!cancelled) {
          setOrders([]);
          setDetail(null);
          setError("Bozor tarixini yuklashda xato yuz berdi.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadOrders();
    return () => {
      cancelled = true;
    };
  }, [searchQuery]);

  useEffect(() => {
    if (!selectedToken) return;

    let cancelled = false;
    async function loadDetail() {
      setDetailLoading(true);
      setError("");
      try {
        const data = await fetchAdminShopOrder(selectedToken);
        if (!cancelled) {
          setDetail(normalizeOrder(data));
        }
      } catch (_) {
        if (!cancelled) {
          setError("Tanlangan tarixni ochishda xato yuz berdi.");
        }
      } finally {
        if (!cancelled) {
          setDetailLoading(false);
        }
      }
    }

    loadDetail();
    return () => {
      cancelled = true;
    };
  }, [selectedToken]);

  const customerRows = useMemo(() => {
    if (!detail) return [];
    return [
      { label: "Ismi", value: detail.full_name || "—" },
      { label: "Telefon", value: detail.phone || "—" },
      { label: "Manzil", value: detail.address || "—" },
      { label: "Izoh", value: detail.comment || "—" },
      { label: "Sotgan admin", value: detail.sold_by_username || "—" },
      { label: "Holati", value: statusLabel(detail.status) },
    ];
  }, [detail]);

  return (
    <div className={styles.wrap}>
      <div className={styles.layout2}>
        <section className={`${styles.orderList} fr-card${mobileView === "detail" ? ` ${styles.hideMobile}` : ""}`}>
          <div className={styles.listHead}>
            <strong>Bozor tarix</strong>
          </div>

          <input
            className={styles.input}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Ism yoki telefon bo'yicha qidiring"
            value={searchTerm}
          />

          <div className={styles.listBody}>
            {!loading && !orders.length ? <div className={styles.empty}>Tarix topilmadi.</div> : null}

            {orders.map((order) => (
              <button
                className={`${styles.orderCard} ${selectedToken === order.group_token ? styles.orderCardActive : ""}`}
                key={order.group_token}
                onClick={() => { setSelectedToken(order.group_token); setMobileView("detail"); }}
                type="button"
              >
                <div className={styles.orderCardTop}>
                  <strong>{order.full_name || "Ism yo'q"}</strong>
                  <span className={styles.dateChip}>{statusLabel(order.status)}</span>
                </div>
                <div className={styles.orderMeta}>
                  <span>{order.phone || "Telefon yo'q"}</span>
                  <span>{formatDate(order.created_at)}</span>
                </div>
                <div className={styles.orderProducts}>
                  {order.items.slice(0, 3).map((item) => item.product_name).join(", ")}
                </div>
              </button>
            ))}

            {loading ? <div className={styles.empty}>Yuklanmoqda...</div> : null}
          </div>
        </section>

        <section className={`${styles.editor} fr-card${mobileView === "list" ? ` ${styles.hideMobile}` : ""}`}>
          <button className={styles.mobileBack} onClick={() => setMobileView("list")}>
            ← Orqaga
          </button>
          <div className={styles.editorToolbar}>
            <div>
              <h3>Bozor tarixi tafsiloti</h3>
              <p>Tasdiqlangan yoki yakunlangan bozor savdolari shu yerda ko'rinadi.</p>
            </div>
            {detail?.group_token ? <span className={styles.token}>#{detail.group_token}</span> : null}
          </div>

          {error ? <div className={styles.errorText}>{error}</div> : null}
          {detailLoading ? <div className={styles.empty}>Tarix yuklanmoqda...</div> : null}

          {!detailLoading && detail ? (
            <>
              <div className={styles.customerGrid}>
                {customerRows.map((row) => (
                  <div className={styles.customerCard} key={row.label}>
                    <small>{row.label}</small>
                    <strong>{row.value}</strong>
                  </div>
                ))}
              </div>

              <div className={styles.itemsPanel} style={{ marginTop: 14 }}>
                <div className={styles.sectionHead}>
                  <strong>Buyurtma mahsulotlari</strong>
                  <small>{detail.items.reduce((sum, item) => sum + item.quantity, 0)} ta</small>
                </div>

                <div className={styles.itemsList}>
                  {!detail.items.length ? <div className={styles.empty}>Mahsulot topilmadi.</div> : null}
                  {detail.items.map((item) => (
                    <div className={styles.saleRow} key={`${detail.group_token}-${item.product_id}`}>
                      <div className={styles.saleCopy}>
                        <strong>{item.product_name}</strong>
                        <span>{item.product_sku || "SKU yo'q"}</span>
                        <small>{formatPrice(item.product_price)}</small>
                      </div>
                      <div className={styles.dateChip}>{item.quantity} ta</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : null}

          {!detailLoading && !detail ? <div className={styles.empty}>Ko'rish uchun tarix tanlang.</div> : null}
        </section>
      </div>
    </div>
  );
}
